import type { OnlineBookTemplate } from '@features/learning/shared/online-book-template'

export type TeachingPresentationSourceTemplate =
  | 'blog'
  | 'conversation'
  | 'heading'
  | 'highlighter'
  | 'image'
  | 'lists'
  | 'multiple_choice'
  | 'pronouncing_practice'
  | 'structure'
  | 'subtitle'
  | 'vocabulary'
  | 'vocabularyWithImage'
  | 'vocabularyWithoutImage'

export type TeachingPresentationSlideTemplate =
  | 'conversation'
  | 'highlighter'
  | 'image'
  | 'list'
  | 'multiple-choice'
  | 'pronunciation'
  | 'structure'
  | 'text'
  | 'title'
  | 'vocabulary'
  | 'vocabulary-compact'
  | 'vocabulary-image'

export type TeachingPresentationLegacySlideKind =
  | 'cards'
  | 'image'
  | 'multiple_choice'
  | 'text'
  | 'title'

export interface TeachingPresentationTemplateDefinition {
  readonly legacyKind: TeachingPresentationLegacySlideKind
  readonly slideTemplate: TeachingPresentationSlideTemplate
  readonly sourceTemplates: readonly TeachingPresentationSourceTemplate[]
}

export const teachingPresentationSlideTemplateRegistry = {
  conversation: {
    legacyKind: 'cards',
    slideTemplate: 'conversation',
    sourceTemplates: ['conversation'],
  },
  highlighter: {
    legacyKind: 'cards',
    slideTemplate: 'highlighter',
    sourceTemplates: ['highlighter'],
  },
  image: {
    legacyKind: 'image',
    slideTemplate: 'image',
    sourceTemplates: ['image'],
  },
  list: {
    legacyKind: 'cards',
    slideTemplate: 'list',
    sourceTemplates: ['lists'],
  },
  'multiple-choice': {
    legacyKind: 'multiple_choice',
    slideTemplate: 'multiple-choice',
    sourceTemplates: ['multiple_choice'],
  },
  pronunciation: {
    legacyKind: 'cards',
    slideTemplate: 'pronunciation',
    sourceTemplates: ['pronouncing_practice'],
  },
  structure: {
    legacyKind: 'cards',
    slideTemplate: 'structure',
    sourceTemplates: ['structure'],
  },
  text: {
    legacyKind: 'text',
    slideTemplate: 'text',
    sourceTemplates: ['blog'],
  },
  title: {
    legacyKind: 'title',
    slideTemplate: 'title',
    sourceTemplates: ['heading', 'subtitle'],
  },
  vocabulary: {
    legacyKind: 'cards',
    slideTemplate: 'vocabulary',
    sourceTemplates: ['vocabularyWithoutImage'],
  },
  'vocabulary-compact': {
    legacyKind: 'cards',
    slideTemplate: 'vocabulary-compact',
    sourceTemplates: ['vocabulary'],
  },
  'vocabulary-image': {
    legacyKind: 'cards',
    slideTemplate: 'vocabulary-image',
    sourceTemplates: ['vocabularyWithImage'],
  },
} as const satisfies Record<TeachingPresentationSlideTemplate, TeachingPresentationTemplateDefinition>

const sourceTemplateToSlideTemplate = {
  blog: 'text',
  conversation: 'conversation',
  heading: 'title',
  highlighter: 'highlighter',
  image: 'image',
  lists: 'list',
  multiple_choice: 'multiple-choice',
  pronouncing_practice: 'pronunciation',
  structure: 'structure',
  subtitle: 'title',
  vocabulary: 'vocabulary-compact',
  vocabularyWithImage: 'vocabulary-image',
  vocabularyWithoutImage: 'vocabulary',
} as const satisfies Record<TeachingPresentationSourceTemplate, TeachingPresentationSlideTemplate>

export function resolveTeachingPresentationSlideTemplate(
  sourceTemplate: OnlineBookTemplate,
): TeachingPresentationSlideTemplate | null {
  if (!Object.prototype.hasOwnProperty.call(sourceTemplateToSlideTemplate, sourceTemplate)) {
    return null
  }

  return sourceTemplateToSlideTemplate[sourceTemplate as TeachingPresentationSourceTemplate]
}

export function getTeachingPresentationTemplateDefinition(
  slideTemplate: TeachingPresentationSlideTemplate,
): TeachingPresentationTemplateDefinition {
  return teachingPresentationSlideTemplateRegistry[slideTemplate]
}
